import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../../../components/Breadcrumb';
import Page from '../../../../components/Page';
import client from '../../../../lib/feathers';

export default function AccessPassEdit() {
  const { apid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pass, setPass] = useState(null);

  const [formData, setFormData] = useState({
    description: '',
    maxUses: '',
    expiresAt: '',
    isActive: true,
  });

  useEffect(() => {
    const loadPass = async () => {
      try {
        const service = client.service('admin/access-pass');
        const result = await service.get(apid);
        setPass(result);
        setFormData({
          description: result.description || '',
          maxUses: result.maxUses || '',
          expiresAt: result.expiresAt ? result.expiresAt.slice(0, 10) : '',
          isActive: result.isActive,
        });
      } catch (e) {
        console.error('Error loading access pass:', e);
        alert('Failed to load access pass.');
        navigate('/admin/access-passes');
      } finally {
        setLoading(false);
      }
    };
    loadPass();
  }, [apid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const service = client.service('admin/access-pass');
      const payload = {
        description: formData.description || null,
        maxUses: formData.maxUses ? parseInt(formData.maxUses, 10) : null,
        expiresAt: formData.expiresAt || null,
        isActive: formData.isActive,
      };
      await service.patch(apid, payload);
      navigate(`/admin/access-passes/${apid}`);
    } catch (err) {
      console.error('Error updating access pass:', err);
      alert(err.message || 'Failed to update access pass');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page title="Edit Access Pass">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Page>
    );
  }

  return (
    <Page title={`Edit Access Pass: ${pass?.code}`}>
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[
          { name: 'Access Passes', href: '/admin/access-passes' },
          { name: pass?.code, href: `/admin/access-passes/${apid}` },
          { name: 'Edit' },
        ]} />
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Access Pass</h3>

          {/* Read-only fields */}
          <div className="mb-6 bg-gray-50 rounded-md p-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-500">Code</span>
                <p className="mt-1 font-mono text-gray-900">{pass?.code}</p>
              </div>
              <div>
                <span className="font-medium text-gray-500">Tier</span>
                <p className="mt-1 text-gray-900 capitalize">{pass?.grantTier}</p>
              </div>
              <div>
                <span className="font-medium text-gray-500">Duration</span>
                <p className="mt-1 text-gray-900">{pass?.durationDays} days</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Internal notes about this access pass..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="maxUses" className="block text-sm font-medium text-gray-700">
                  Max Uses
                </label>
                <input
                  type="number"
                  id="maxUses"
                  min="1"
                  value={formData.maxUses}
                  onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                  placeholder="Unlimited"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700">
                  Expiry Date
                </label>
                <input
                  type="date"
                  id="expiresAt"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="isActive" className="font-medium text-gray-700">
                  Active
                </label>
                <p className="text-gray-500">
                  Disable to prevent new redemptions without deleting the pass
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(`/admin/access-passes/${apid}`)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Page>
  );
}
